// DataProvider — the seam that lets an app OUTGROW s3db without a rewrite.
//
// `app.collection` / `app.doc` are façades over a provider. The default is
// S3 (s3db): no server, IAM is the authorization layer. When an app scales
// past what S3 semantics can carry (heavy secondary-index churn, real
// transactions, cross-user read isolation at field level, push subscriptions),
// it swaps the provider — the app's collection/doc CALL SITES don't change.
//
// THE HONEST CAVEAT (see docs/DATA-PROVIDER.md): the "no server, IAM-is-authz"
// property does NOT port. A Postgres provider necessarily introduces a
// request-path service (the project runtime's HTTP surface) that verifies the
// Cognito JWT and lets Row-Level Security enforce per-user scoping. That's the
// price of the swap; the interface doesn't pretend otherwise. What DOES port
// is the app's code and the grants manifest (`s3p.permissions.json` grants name STORES,
// not S3 globs — one spec, two enforcement backends: IAM today, RLS then).
//
// Contracts are stated at the INTERFACE level, never in S3 terms, so a
// stronger backend is always a legal implementation:
//
//   update(mutator) → compare-and-swap, retried on conflict
//                     (ETag/If-Match on S3; `WHERE version = …` on SQL)
//   unique          → enforced; mechanism unspecified
//                     (If-None-Match:* on S3; a unique index on SQL)
//   subscribe       → eventual change notification, latency unspecified
//                     (LIST polling on S3; LISTEN/NOTIFY on SQL)
//   findBy          → secondary lookup; MAY be eventually consistent on S3,
//                     IS consistent on SQL
//
// `view()` and `transaction()` are deliberately NOT part of this interface:
// DuckDB-over-parquet views and best-effort compensating "transactions" are
// S3-specific. They're capability-gated (see `capabilities`) so an app can
// branch instead of silently getting different semantics.

import type { Collection, CollectionConfig } from "./collection.ts";
import type { DocConfig, DocHandle } from "./doc.ts";

/** Where a store lives. `shared` = readable by every signed-in user of the
 *  app (the platform's shared-by-default posture); `private` = the caller's
 *  own space, unreadable and unlistable by other members. */
/** Where a collection/doc/log lives, and therefore WHO CAN SEE IT — enforced by
 *  IAM on the prefix, not by code.
 *
 *    shared   every signed-in member can read (the default)
 *    private  only the owning user — `s3db/private/{sub}/`
 *    admin    only roles holding `read-all` — `s3db/admin/`
 *
 *  `admin` exists because there was nothing between "everyone" and "one person".
 *  Every app eventually needs staff-only data — orders, tickets, moderation
 *  queues — and without this the only options were to expose it to all members or
 *  bury it in one user's private space. (Our own feedback reports, which carry
 *  reporter emails, were the first thing to need it.) */
export type DataScope = "shared" | "private" | "admin";

export interface ProviderCapabilities {
  /** Real ACID transactions across keys. S3: false (best-effort
   *  compensation). SQL: true. */
  atomicTx: boolean;
  /** In-engine analytical queries (`app.view`). S3: true, via DuckDB-WASM
   *  over parquet rollups. SQL: true, as plain SQL (different shape). */
  analytics: boolean;
  /** Push-based change notification. S3: false (subscribe polls). SQL: true. */
  liveSubscribe: boolean;
  /** Server-enforced per-row/field authorization independent of storage
   *  paths. S3: false (IAM path scoping is the whole model). SQL: true (RLS). */
  rowLevelSecurity: boolean;
}

export interface CollectionOptions<T> extends CollectionConfig<T> {
  scope?: DataScope;
  /** Group records by who wrote them. INTERFACE-level contract: `put`
   *  writes into the caller's own group; reads span all groups; the
   *  backend enforces "write own" (S3: the author sub is a key segment
   *  matched by IAM; SQL: an author column + RLS). This is what the
   *  manifest's `"<store>/own"` grants compile against. */
  partition?: "author";
}
export interface DocOptions extends DocConfig {
  scope?: DataScope;
}

/** What every backend must provide. Deliberately small: the surface an app
 *  actually writes against, nothing S3-shaped. */
export interface DataProvider {
  readonly name: string;
  readonly capabilities: ProviderCapabilities;
  collection<T = unknown>(name: string, cfg?: CollectionOptions<T>): Collection<T>;
  doc<T = unknown>(name: string, cfg?: DocOptions): DocHandle<T>;
}

/** The capability set of the built-in S3 (s3db) provider — exported so app
 *  code and tests can assert against it without constructing a client. */
export const S3_CAPABILITIES: ProviderCapabilities = {
  atomicTx: false,
  analytics: true,
  liveSubscribe: false,
  rowLevelSecurity: false,
};

/** Guard for code paths that need a capability the active provider lacks.
 *  Throws a message that names the provider and the fix, instead of failing
 *  later in a confusing way. */
export function requireCapability(
  provider: DataProvider,
  cap: keyof ProviderCapabilities,
): void {
  if (!provider.capabilities[cap]) {
    throw new Error(
      `provider '${provider.name}' does not support '${cap}'. ` +
        "Either avoid this operation or migrate the project to a provider that does " +
        "(see docs/DATA-PROVIDER.md).",
    );
  }
}
